import { useState, useMemo, createContext, useContext, Fragment } from "react";
import { useHistory, useParams } from "react-router-dom";
import { PreloadImgs } from "Components";
import useGetData from "hooks/useGetData";
import { apiGetCompanyDetail } from "services/api/company";
import { PATHS, SEARCH_PARAMS } from "constants/routes";
import { apiGetFarmPosts } from "services/api/smartTrace";
import useNavigate from "hooks/useNavigate";
import { ParamsType } from "types/params";
import StickyBottomSection from "Components/bar/StickyBottomSection";
import FreePage from "./components/FreePage";
import { isNotFreePlan } from "helpers/plan";
import PaidPage from "./components/PaidPage";
import FreshdiLoading from "Components/loading/FreshdiLoading";
import Breadcrumbs from "Components/breadcrumbs";
import { Dialog } from "@mui/material";
import { ClaimYourBusinessForm } from "Components/form";
import { REPORT_TYPE } from "constants/data";

const SupplierContext = createContext<any>({});

export const useSupplierContext = () => useContext(SupplierContext);

const LIMIT_POST = 10;

const Farm = () => {
  const { supplierId } = useParams<ParamsType>();
  const history = useHistory();
  const { useSearchParams } = useNavigate();

  const [reloadDetail, setReloadDetail] = useState(false);
  const [selectedTab, setSelectedTab] = useState(
    useSearchParams.get(SEARCH_PARAMS.tab) || ""
  );
  const [filterPost, setFilterPost] = useState({
    offset: 0,
    limit: LIMIT_POST,
  });
  const [showReport, setShowReport] = useState(false);

  const { data: supplierDetailData, isLoading } = useGetData({
    promise: () => {
      if (supplierId) {
        return apiGetCompanyDetail(supplierId).catch((err) => {
          history.replace(PATHS.notFound);
        });
      }
      return null;
    },
    deps: [supplierId, reloadDetail],
  });

  const { isLoading: isLoadingPost, data: postData } = useGetData({
    promise: () =>
      supplierId
        ? apiGetFarmPosts({ ...filterPost, farm_id: supplierId })
        : null,
    deps: [supplierId, filterPost],
  });

  const supplierDetail = useMemo(() => {
    const data = supplierDetailData?.data?.farm || {};
    return { ...data, farm_id: data?.name };
  }, [supplierDetailData?.data]);

  const isPaidSupplier = useMemo(
    () => isNotFreePlan(supplierDetail?.plan),
    [supplierDetail?.plan]
  );

  const onReloadDetail = () => setReloadDetail(!reloadDetail);

  const contextData = {
    postData: postData?.data,
    supplierDetail,
    setSelectedTab,
    selectedTab,
    isLoadingPost,
    setFilterPost,
    filterPost,
    onReloadDetail,
  };

  return (
    <SupplierContext.Provider value={contextData}>
      <PreloadImgs
        images={[
          { url: "/images/background/supplier_bg_en_desktop.webp" },
          { url: "/images/background/supplier_bg_vi_desktop.webp" },
          { url: "/images/background/supplier_bg_en_mobile.webp" },
          { url: "/images/background/supplier_bg_vi_mobile.webp" },
        ]}
      />
      <Breadcrumbs data={supplierDetail?.breadcrumb} />

      {isLoading && <FreshdiLoading />}

      {!isLoading && (
        <Fragment>
          {/* Free supplier viewer */}
          {!isPaidSupplier && <FreePage setShowReport={setShowReport}/>}

          {/* Premium supplier viewer */}
          {isPaidSupplier && <PaidPage />}
        </Fragment>
      )}

      {/* Sticky bottom bar */}
      <StickyBottomSection />
      <Dialog open={showReport} onClose={() => setShowReport(false)}>
        <ClaimYourBusinessForm
          type={REPORT_TYPE.supplier}
          title={supplierDetail.farm_name}
          reference_name={supplierId}
          onClose={() => setShowReport(false)}
        />
      </Dialog>
    </SupplierContext.Provider>
  );
};

export default Farm;
